import React from 'react';
import SEO from '../../../components/SEO/SEO';

import { Link } from 'react-router-dom';
import './Healthcare.css';

const Healthcare = () => {
    return (
        <div className="industry-page">
            <SEO 
                title="Healthcare Services | Digi Network" 
                description="Learn more about our Healthcare services to grow your business online with Digi Network." 
                canonical="/industries/healthcare/healthcare" 
            />

            {/* Hero Section */}
            <section className="industry-hero">
                <div className="industry-hero-bg"></div>
                <div className="container">
                    <div className="industry-hero-content">
                        <div className="label">Healthcare Industry</div>
                        <h1>Digital Marketing for Healthcare</h1>
                        <p>Build trust, attract patients, and grow your healthcare practice with compliant, HIPAA-aware digital marketing strategies designed specifically for the medical industry. We understand the unique challenges healthcare providers face in today's digital landscape.</p>
                        <div className="industry-hero-cta">
                            <Link to="/contact" className="btn btn-primary">
                                Get Started
                                <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                                    <path d="M7.5 15L12.5 10L7.5 5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                </svg>
                            </Link>
                            <Link to="/case-studies" className="btn btn-outline">View Results</Link>
                        </div>
                    </div>
                </div>
            </section>

            {/* Image Section - Hero Image */}
            <section className="hero-image-section">
                <div className="container">
                    <div className="image-placeholder-large">
                        {/* IMAGE PLACEHOLDER: Add a professional healthcare hero image here
                            Recommended: Modern medical facility, doctor with patient, or medical team
                            Size: 100% width, 500px height
                            Format: JPG or PNG
                        */}
                        <p>📷 Add Healthcare Hero Image (1200x500px)</p>
                    </div>
                </div>
            </section>

            {/* Services Grid */}
            <section className="industry-services">
                <div className="container">
                    <h2>Healthcare Marketing Services</h2>
                    <p className="section-intro">Comprehensive marketing solutions tailored for healthcare practices</p>
                    <div className="services-grid">
                        <div className="service-card">
                            <h3>Healthcare SEO</h3>
                            <p>Rank higher for medical keywords, attract local patients, and build authority in your specialty. Our healthcare SEO strategies are designed to help medical practices dominate search results for relevant medical terms and local searches.</p>
                            <ul>
                                <li>Local SEO for medical practices</li>
                                <li>HIPAA-compliant content strategy</li>
                                <li>Medical keyword research</li>
                                <li>Google My Business optimization</li>
                                <li>Medical directory listings</li>
                                <li>Review management</li>
                            </ul>
                            <Link to="/industries/healthcare/seo" className="service-link">Learn More →</Link>
                        </div>

                        <div className="service-card">
                            <h3>Healthcare Paid Ads</h3>
                            <p>Compliant advertising campaigns that attract qualified patients and drive appointments. We create targeted ad campaigns that comply with healthcare advertising regulations while maximizing patient acquisition.</p>
                            <ul>
                                <li>Google Ads for medical practices</li>
                                <li>Facebook & Instagram healthcare ads</li>
                                <li>HIPAA-compliant landing pages</li>
                                <li>Patient acquisition campaigns</li>
                                <li>Retargeting strategies</li>
                                <li>Conversion tracking & optimization</li>
                            </ul>
                            <Link to="/industries/healthcare/paid-ads" className="service-link">Learn More →</Link>
                        </div>

                        <div className="service-card">
                            <h3>Healthcare Website Design</h3>
                            <p>Patient-friendly websites that build trust, showcase expertise, and convert visitors into appointments. Our healthcare websites are designed with patient needs in mind, featuring intuitive navigation and clear calls-to-action.</p>
                            <ul>
                                <li>HIPAA-compliant web design</li>
                                <li>Online appointment booking</li>
                                <li>Patient portal integration</li>
                                <li>Mobile-responsive design</li>
                                <li>Telehealth integration</li>
                                <li>Accessibility compliance</li>
                            </ul>
                            <Link to="/industries/healthcare/web-development" className="service-link">Learn More →</Link>
                        </div>

                        <div className="service-card">
                            <h3>Patient Email Marketing</h3>
                            <p>Nurture patient relationships with educational content, appointment reminders, and follow-up campaigns. Build lasting relationships with patients through strategic email communication that provides value and keeps your practice top-of-mind.</p>
                            <ul>
                                <li>HIPAA-compliant email campaigns</li>
                                <li>Appointment reminders</li>
                                <li>Health education content</li>
                                <li>Patient retention automation</li>
                                <li>Newsletter campaigns</li>
                                <li>Wellness program emails</li>
                            </ul>
                            <Link to="/industries/healthcare/email-marketing" className="service-link">Learn More →</Link>
                        </div>

                        <div className="service-card">
                            <h3>Healthcare CRM & Automation</h3>
                            <p>Streamline patient communication, manage leads, and automate follow-ups with GoHighLevel. Automate your patient communication workflows while maintaining HIPAA compliance and improving patient satisfaction.</p>
                            <ul>
                                <li>Patient lead management</li>
                                <li>Automated appointment reminders</li>
                                <li>HIPAA-compliant CRM setup</li>
                                <li>Multi-channel patient communication</li>
                                <li>Follow-up automation</li>
                                <li>Patient satisfaction tracking</li>
                            </ul>
                            <Link to="/industries/healthcare/all-in-one" className="service-link">Learn More →</Link>
                        </div>
                    </div>
                </div>
            </section>

            {/* Process Section */}
            <section className="process-section">
                <div className="container">
                    <h2 className="process-item-title">Our Healthcare Marketing Process</h2>
                    <p className="section-intro">A proven methodology designed specifically for healthcare providers</p>
                    <div className="process-grid">
                        <div className="process-item">
                            <div className="process-number">1</div>
                            <h3>Discovery & Analysis</h3>
                            <p>We start by understanding your practice, target patients, and competitive landscape. Our team conducts comprehensive audits of your current online presence, patient journey, and marketing performance.</p>
                        </div>
                        <div className="process-item">
                            <div className="process-number">2</div>
                            <h3>Strategy Development</h3>
                            <p>Based on our analysis, we develop a customized marketing strategy that aligns with your practice goals. Every tactic is designed with HIPAA compliance and patient privacy in mind.</p>
                        </div>
                        <div className="process-item">
                            <div className="process-number">3</div>
                            <h3>Implementation</h3>
                            <p>Our team implements the marketing strategy across all channels, ensuring consistent messaging and brand representation. We handle everything from website updates to ad campaign launches.</p>
                        </div>
                        <div className="process-item">
                            <div className="process-number">4</div>
                            <h3>Optimization & Growth</h3>
                            <p>We continuously monitor performance, analyze patient feedback, and optimize campaigns for better results. Regular reporting keeps you informed of your marketing ROI and patient acquisition metrics.</p>
                        </div>
                    </div>
                </div>
            </section>

            {/* Image Section - Process Image */}
            <section className="image-section">
                <div className="container">
                    <div className="image-placeholder-medium">
                        {/* IMAGE PLACEHOLDER: Add a healthcare marketing process image
                            Recommended: Team meeting, medical consultation, or patient care
                            Size: 100% width, 400px height
                            Format: JPG or PNG
                        */}
                        <p>📷 Add Healthcare Process Image (1200x400px)</p>
                    </div>
                </div>
            </section>

            {/* Why Choose Us */}
            <section className="why-choose">
                <div className="container">
                    <h2>Why Healthcare Practices Choose Digi Network</h2>
                    <p className="why-intro">Trusted by medical professionals across the UAE and internationally</p>
                    <div className="why-grid">
                        <div className="why-item">
                            <div className="why-number">01</div>
                            <h3>HIPAA Compliance</h3>
                            <p>All our marketing strategies are designed with HIPAA compliance in mind, protecting patient privacy while growing your practice. We understand the strict regulations governing healthcare marketing and ensure every campaign meets these standards.</p>
                        </div>
                        <div className="why-item">
                            <div className="why-number">02</div>
                            <h3>Healthcare Expertise</h3>
                            <p>We understand the unique challenges of healthcare marketing, from patient acquisition to reputation management. Our team has extensive experience working with medical practices, hospitals, and healthcare organizations.</p>
                        </div>
                        <div className="why-item">
                            <div className="why-number">03</div>
                            <h3>Proven Results</h3>
                            <p>We've helped healthcare practices increase patient appointments by 300%+ through targeted digital marketing. Our data-driven approach delivers measurable results that directly impact your bottom line.</p>
                        </div>
                        <div className="why-item">
                            <div className="why-number">04</div>
                            <h3>Transparent Reporting</h3>
                            <p>Monthly performance reports showing patient acquisition, appointment bookings, and ROI metrics. We provide detailed analytics and insights to help you understand exactly how your marketing is performing.</p>
                        </div>
                    </div>
                </div>
            </section>

            {/* Benefits Section */}
            <section className="benefits-section">
                <div className="container">
                    <h2>Key Benefits for Healthcare Practices</h2>
                    <p className="section-intro">How our marketing services help you grow your practice</p>
                    <div className="benefits-grid">
                        <div className="benefit-item">
                            <h3>Increased Patient Acquisition</h3>
                            <p>Attract more qualified patients through targeted marketing campaigns that reach people actively searching for your services. Our strategies focus on converting high-intent searches into appointments.</p>
                        </div>
                        <div className="benefit-item">
                            <h3>Improved Online Visibility</h3>
                            <p>Dominate search results for relevant medical keywords and local searches. When potential patients search for your services, they'll find your practice at the top of results.</p>
                        </div>
                        <div className="benefit-item">
                            <h3>Enhanced Patient Trust</h3>
                            <p>Build credibility and trust through professional online presence, positive reviews, and educational content. Patients are more likely to choose practices with strong online reputations.</p>
                        </div>
                        <div className="benefit-item">
                            <h3>Streamlined Operations</h3>
                            <p>Automate patient communication, appointment reminders, and follow-ups to reduce administrative burden and improve patient satisfaction.</p>
                        </div>
                        <div className="benefit-item">
                            <h3>Better ROI Tracking</h3>
                            <p>Understand exactly which marketing channels deliver the best results with comprehensive tracking and reporting. Make data-driven decisions about your marketing spend.</p>
                        </div>
                        <div className="benefit-item">
                            <h3>Competitive Advantage</h3>
                            <p>Stay ahead of competitors with cutting-edge marketing strategies and technology. Many healthcare practices still rely on outdated marketing methods.</p>
                        </div>
                    </div>
                </div>
            </section>

            {/* Image Section - Benefits Image */}
            <section className="image-section">
                <div className="container">
                    <div className="image-placeholder-medium">
                        {/* IMAGE PLACEHOLDER: Add a healthcare benefits/team image
                            Recommended: Healthcare team, modern medical equipment, or patient care
                            Size: 100% width, 400px height
                            Format: JPG or PNG
                        */}
                        <p>📷 Add Healthcare Benefits Image (1200x400px)</p>
                    </div>
                </div>
            </section>

            {/* CTA Section */}
            <section className="industry-cta">
                <div className="container">
                    <h2>Ready to Grow Your Healthcare Practice?</h2>
                    <p>Let's discuss how we can help you attract more patients and build your medical practice online.</p>
                    <Link to="/contact" className="btn btn-primary">
                        Book a Free Consultation
                        <svg width="25" height="23" viewBox="0 0 25 23" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M24.4766 12.1562L14.8516 21.7812C14.5234 22.1094 13.9219 22.1094 13.5938 21.7812C13.2656 21.4531 13.2656 20.8516 13.5938 20.5234L21.7422 12.375H1.125C0.632812 12.375 0.25 11.9922 0.25 11.5C0.25 11.0625 0.632812 10.625 1.125 10.625H21.7422L13.5938 2.53125C13.2656 2.20312 13.2656 1.60156 13.5938 1.27344C13.9219 0.945312 14.5234 0.945312 14.8516 1.27344L24.4766 10.8984C24.8047 11.2266 24.8047 11.8281 24.4766 12.1562Z" fill="white"/>
                        </svg>
                    </Link>
                </div>
            </section>
        </div>
    );
};

export default Healthcare;

